import type { ReactNode } from 'react';
import { cn } from '@perch/ui-tokens';

interface DataTableProps {
  children: ReactNode;
  className?: string;
  borderColor?: 'default' | 'emerald';
  footer?: ReactNode;
  /** Render without the outer container (border/bg/radius). Use when embedding inside a custom wrapper. */
  bare?: boolean;
}

export function DataTable({ children, className, borderColor = 'default', footer, bare }: DataTableProps) {
  const table = (
    <div className="overflow-x-auto">
      <table className={cn('w-full data-table', className)}>
        {children}
      </table>
    </div>
  );

  if (bare) return table;

  return (
    <div className={cn(
      'overflow-hidden rounded-xl bg-white dark:bg-stone-900 border',
      borderColor === 'emerald'
        ? 'border-emerald-200 dark:border-emerald-800/50'
        : 'border-stone-200 dark:border-stone-700',
    )}>
      {table}
      {footer}
    </div>
  );
}

interface DataTableHeaderProps {
  children: ReactNode;
  color?: 'default' | 'emerald';
  className?: string;
}

export function DataTableHeader({ children, color = 'default', className }: DataTableHeaderProps) {
  return (
    <thead>
      <tr className={cn(
        color === 'emerald' && 'bg-emerald-50 dark:bg-emerald-900/20',
        className,
      )}>
        {children}
      </tr>
    </thead>
  );
}

export interface DataTableBodyProps {
  children?: ReactNode;
  emptyMessage?: string;
  colSpan?: number;
}

export function DataTableBody({ children, emptyMessage, colSpan = 7 }: DataTableBodyProps) {
  const hasChildren = Array.isArray(children)
    ? children.filter(Boolean).length > 0
    : !!children;

  return (
    <tbody>
      {hasChildren ? children : (
        <tr>
          <td colSpan={colSpan} className="text-center py-8 text-stone-400">
            {emptyMessage || 'No data'}
          </td>
        </tr>
      )}
    </tbody>
  );
}
